import { FC, useContext, useEffect, useRef, useState } from 'react';
import { Box, IconButton, Popover, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import BasicToggleButtonGroup from '../common/BasicToggleButtonGroup';
import BasicSearchBar from '../common/BasicSearchBar';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import SortIcon from '@mui/icons-material/Sort';
import FilterCampaignBox from './FilterCampaignBox';
import SortCampaignBox from './SortCampaignBox';
import { CampaignContext } from '../../contexts/CampaignContext';
import { formatPricePersian } from '../../utils/number';
import axios from 'axios';
import IResponse from '../../interfaces/response';
import { sumPrice } from '../../utils/sumPrice';
import { useDebounce } from 'use-debounce';
import { DELAY_BEFORE_SEARCH } from '../../constants/numbers';
import { useParams } from 'react-router-dom';

const sortItemTexts = [
	{ title: 'most_expensive_package_channels', orderBy: 'price', orderByDirection: 'asc' },
	{ title: 'cheep_package_channels', orderBy: 'price', orderByDirection: 'desc' },
	{ title: 'most_er', orderBy: 'er', orderByDirection: 'desc' },
	{ title: 'most_members_followers', orderBy: 'followers_count', orderByDirection: 'desc' },
];

type ICreateCampaignModalHeader = {
	filterCategory: any;
	searchTerm: string;
	setFilterCategory: (val: any) => void;
	onSearch: (term: string) => void;
};

const CreateCampaignModalHeader: FC<ICreateCampaignModalHeader> = ({ filterCategory, setFilterCategory, onSearch, searchTerm }) => {
	const { type } = useParams();
	const ButtonFilter = useRef(null);
	const [search] = useDebounce(searchTerm, DELAY_BEFORE_SEARCH);
	const [filtersAnchorEl, setFiltersAnchorEL] = useState<HTMLButtonElement | null>(null);
	const [sortAnchorEl, setSortAnchorEL] = useState<HTMLButtonElement | null>(null);
	// const [selectedId, setSelectedId] = useState<{ orderBy: string; orderByDirection: string } | >(null);

	const { t } = useTranslation();

	const { selectedPlatform, selectedPages, selectedPackages, typeActiveButton, selectedPlatformId, categoryActiveButton, setFetchedEntites, setCategoryActiveButton, setFetchedEntitiesPagination, setTypeActiveButton, setSelectedPackages, setSelectedPages, setFetchedPackages } = useContext(CampaignContext);

	const pageTabTitle = selectedPlatform === 'instagram' ? t('page') : t('channel');
	const activeButtonClasses = 'rounded-md bg-primary-main font-normal text-primary-contrastText h-full';

	const filtersOpen = Boolean(filtersAnchorEl);
	const sortOpen = Boolean(sortAnchorEl);
	const filtersId = filtersOpen ? 'simple-popover' : undefined;
	const sortId = sortOpen ? 'simple-popover' : undefined;

	let sum_package_price = sumPrice(selectedPackages);
	let sum_page_price = sumPrice(selectedPages);
	// const handleSelect = (id: { orderBy: string; orderByDirection: string }) => {
	// 	setSelectedId(id);
	// 	setTimeout(() => {
	// 		setSortAnchorEL(null);
	// 	}, 10);
	// };

	// useEffect(() => {
	// 	let sortedPackages = sortItems(sortItemTexts[selectedId - 1], fetchedPackages);
	// 	let sortedEntities = sortItems(sortItemTexts[selectedId - 1], fetchedEntities);
	// 	setFetchedPackages(sortedPackages as IPackage[]);
	// 	setFetchedEntites(sortedEntities as IEntity[]);
	// }, [selectedId]);

	useEffect(() => {
		setTypeActiveButton(1);
	}, [selectedPlatform]);

	const toggleTypeButton = (_: React.SyntheticEvent, newValue: number) => {
		if (selectedPlatformId !== 10) {
			setSelectedPages([]);
		}
		setSelectedPackages([]);
		if (newValue !== null) {
			setTypeActiveButton(newValue);
		}
	};

	const toggleCategoryButton = (_: React.SyntheticEvent, newValue: number) => {
		// if (selectedPlatformId !== 10) {
		// 	setSelectedPages([]);
		// }
		setSelectedPackages([]);
		if (newValue !== null) {
			setCategoryActiveButton(newValue);
		}
	};

	const handleOpenFiltersPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
		setFiltersAnchorEL(event.currentTarget);
	};

	const handleOpenSortPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
		setSortAnchorEL(event.currentTarget);
	};

	const handleCloseFiltersPopover = () => {
		setTimeout(() => {
			setFiltersAnchorEL(null);
		}, 10);
	};

	const handleCloseSortPopover = () => {
		setTimeout(() => {
			setSortAnchorEL(null);
		}, 10);
	};

	const handleSearchPackage = () => {
		axios<IResponse<any>>({
			method: 'get',
			url: typeActiveButton === 0 ? `/media_packages` : `/media_entities`,
			params: {
				campaign_type: type,
				title: searchTerm,
				media_id: selectedPlatformId?.toString(),
			},
		}).then(res => {
			if (res.data.success) {
				if (typeActiveButton === 0) {
					setFetchedPackages(res.data.data);
				} else {
					setFetchedEntitiesPagination(res.data.pagination);
					setFetchedEntites(res.data.data);
				}
			}
		});
	};

	useEffect(() => {
		handleSearchPackage();
	}, [search, typeActiveButton]);

	const isProvincialActive = typeActiveButton === 1 && categoryActiveButton === 1;
	return (
		<Box className="mx-3 my-8 flex h-[50px] items-center justify-between">
			<Box className="flex h-full items-center justify-start gap-x-4">
				<BasicToggleButtonGroup activeButton={typeActiveButton} handleToggle={toggleTypeButton} activeButtonClasses={activeButtonClasses} titles={[t('package'), pageTabTitle]} />
				{typeActiveButton === 1 && <BasicToggleButtonGroup activeButton={categoryActiveButton} handleToggle={toggleCategoryButton} activeButtonClasses={activeButtonClasses} titles={[t('subject'), t('provincial')]} />}
				<Box className="flex h-full items-center justify-center rounded bg-action-selected px-5 text-sm font-medium text-text-primary">{`${t('reserves_count')} :  ${selectedPackages.length > 0 ? selectedPackages.length : selectedPages.length}`}</Box>
				<Box className="flex h-full items-center justify-center rounded bg-action-selected px-5 text-sm font-medium text-text-primary">{`${t('total_cost')} :  ${selectedPackages && selectedPackages.length > 0 ? formatPricePersian(sum_package_price) : formatPricePersian(sum_page_price)} ${t('tooman')}`}</Box>
			</Box>
			<Box className={`flex h-full ${!isProvincialActive && 'grow basis-0'} items-center justify-end gap-x-4 ps-24`}>
				<IconButton className="p-0" onClick={handleOpenSortPopover}>
					<SortIcon className="h-8 w-8 text-action-active" />
					<Popover
						onClose={handleCloseSortPopover}
						anchorEl={sortAnchorEl}
						open={sortOpen}
						id={sortId}
						anchorOrigin={{
							vertical: 'bottom',
							horizontal: 'center',
						}}
						transformOrigin={{
							vertical: 'top',
							horizontal: 'center',
						}}
					>
						<Box className="px-2 py-4">
							<SortCampaignBox texts={sortItemTexts} />
						</Box>
					</Popover>
				</IconButton>
				{typeActiveButton === 1 && (
					<IconButton className="p-0" ref={ButtonFilter} onClick={handleOpenFiltersPopover}>
						<FilterAltIcon className="h-8 w-8 text-action-active" />
						<Popover
							onClose={handleCloseFiltersPopover}
							open={filtersOpen}
							id={filtersId}
							anchorEl={filtersAnchorEl}
							anchorOrigin={{
								vertical: 'bottom',
								horizontal: 'center',
							}}
							transformOrigin={{
								vertical: 'top',
								horizontal: 'center',
							}}
						>
							<Box className="p-6">
								<Typography className="mb-3 font-medium">
									{t('filter_the_list_based_on')} : {categoryActiveButton === 0 ? t('subject') : t('province')}
								</Typography>
								<FilterCampaignBox setFiltersAnchorEL={setFiltersAnchorEL} isProvincialActive={isProvincialActive} value={filterCategory} setValue={setFilterCategory} />
							</Box>
						</Popover>
					</IconButton>
				)}
				<BasicSearchBar onSearch={onSearch} searchTerm={searchTerm} />
			</Box>
		</Box>
	);
};

export default CreateCampaignModalHeader;
